import { initializeApp } from "firebase/app";
import {
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager
} from "firebase/firestore";
import { connectFirestoreEmulator, memoryLocalCache } from "firebase/firestore";
import { connectFunctionsEmulator, getFunctions } from "firebase/functions";
import { connectAuthEmulator, getAuth } from "firebase/auth";
import { connectStorageEmulator, getStorage } from "firebase/storage";

const PRODUCTION_FALLBACK_CONFIG = {
  apiKey: "AIzaSyDOjotfwLgzH0io2GZqqZC88nHMmxB1ERM",
  authDomain: "trading-journal-app-d492e.firebaseapp.com",
  projectId: "trading-journal-app-d492e",
  storageBucket: "trading-journal-app-d492e.firebasestorage.app",
  messagingSenderId: "138750603677",
  appId: "1:138750603677:web:4d3e4ab3912c349e4bff74",
  measurementId: "G-M1GEFJYEGS"
};

const isBrowser = typeof window !== "undefined";
const host = isBrowser ? window.location.hostname : "";
const isLocalhost = host === "localhost" || host === "127.0.0.1" || host === "::1";

// Localhost Firebase behavior:
// - If a full DEV config is provided, localhost can use it.
// - If DEV config is missing, fall back to production (no crash/blank screen).
const allowProdOnLocalhost = process.env.REACT_APP_ALLOW_PROD_FIREBASE_ON_LOCALHOST === "true";
const preferDevOnLocalhost = process.env.REACT_APP_USE_DEV_FIREBASE_ON_LOCALHOST !== "false";

const devConfig = {
  apiKey: process.env.REACT_APP_DEV_FIREBASE_API_KEY || "",
  authDomain: process.env.REACT_APP_DEV_FIREBASE_AUTH_DOMAIN || "",
  projectId: process.env.REACT_APP_DEV_FIREBASE_PROJECT_ID || "",
  storageBucket: process.env.REACT_APP_DEV_FIREBASE_STORAGE_BUCKET || "",
  messagingSenderId: process.env.REACT_APP_DEV_FIREBASE_MESSAGING_SENDER_ID || "",
  appId: process.env.REACT_APP_DEV_FIREBASE_APP_ID || "",
  measurementId: process.env.REACT_APP_DEV_FIREBASE_MEASUREMENT_ID || ""
};

const prodConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || PRODUCTION_FALLBACK_CONFIG.apiKey,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || PRODUCTION_FALLBACK_CONFIG.authDomain,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || PRODUCTION_FALLBACK_CONFIG.projectId,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || PRODUCTION_FALLBACK_CONFIG.storageBucket,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || PRODUCTION_FALLBACK_CONFIG.messagingSenderId,
  appId: process.env.REACT_APP_FIREBASE_APP_ID || PRODUCTION_FALLBACK_CONFIG.appId,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || PRODUCTION_FALLBACK_CONFIG.measurementId
};

const hasCompleteDevConfig = Boolean(
  devConfig.apiKey &&
  devConfig.authDomain &&
  devConfig.projectId &&
  devConfig.storageBucket &&
  devConfig.messagingSenderId &&
  devConfig.appId
);

// Local Emulator Suite mode (docs/LOCAL_PREVIEW.md). Only honoured on localhost so a
// stray build flag can never point a deployed app at emulators. Uses a `demo-` project
// id, which the Firebase SDK and emulators treat as offline-only.
const useEmulators = isLocalhost && process.env.REACT_APP_USE_FIREBASE_EMULATORS === "true";
const EMULATOR_PROJECT_ID = process.env.REACT_APP_EMULATOR_PROJECT_ID || "demo-bandz";
const emulatorConfig = {
  apiKey: "demo-api-key",
  authDomain: `${EMULATOR_PROJECT_ID}.firebaseapp.com`,
  projectId: EMULATOR_PROJECT_ID,
  storageBucket: `${EMULATOR_PROJECT_ID}.appspot.com`,
  messagingSenderId: "0",
  appId: "1:0:web:0"
};

let firebaseConfig = prodConfig;
let firebaseTarget = "production";

if (useEmulators) {
  firebaseConfig = emulatorConfig;
  firebaseTarget = "emulator";
} else if (isLocalhost && preferDevOnLocalhost && !allowProdOnLocalhost) {
  if (hasCompleteDevConfig) {
    firebaseConfig = devConfig;
    firebaseTarget = "development";
  } else {
    console.warn(
      "[FIREBASE] DEV config not found on localhost; falling back to production Firebase. " +
      "Set REACT_APP_DEV_FIREBASE_* vars to isolate local data."
    );
  }
}

const app = initializeApp(firebaseConfig);
const db = initializeFirestore(app, {
  localCache: useEmulators
    ? memoryLocalCache()
    : persistentLocalCache({
      tabManager: persistentMultipleTabManager()
    })
});
console.info(`[FIREBASE] Connected to ${firebaseTarget} project: ${firebaseConfig.projectId}`);
const auth = getAuth(app);
const storage = getStorage(app);
const functions = getFunctions(app);

if (useEmulators) {
  const port = (name, fallback) => Number(process.env[`REACT_APP_EMULATOR_${name}_PORT`]) || fallback;
  connectFirestoreEmulator(db, "127.0.0.1", port("FIRESTORE", 8080));
  connectAuthEmulator(auth, `http://127.0.0.1:${port("AUTH", 9099)}`, { disableWarnings: true });
  connectStorageEmulator(storage, "127.0.0.1", port("STORAGE", 9199));
  connectFunctionsEmulator(functions, "127.0.0.1", port("FUNCTIONS", 5001));

  // Dev helper for scripted QA: signs in (creating if needed) an emulator-only user.
  window.__bandzEmulatorSignIn = async (email, password = "emulator-only-pass") => {
    const { signInWithEmailAndPassword, createUserWithEmailAndPassword } = await import("firebase/auth");
    try {
      return await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      return createUserWithEmailAndPassword(auth, email, password);
    }
  };
}

export { app, db, auth, storage, functions };
